
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/Layout";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";
import ActionButton from "../components/ActionButton";
import PageTransition from "../components/PageTransition";
import VideoModal from "../components/VideoModal";
import { toast } from "@/hooks/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { setVideoActor, clearScriptAudio } from "@/store/features/videoSlice";
import { Loader } from "lucide-react";
import { useRollbarPerson } from "@rollbar/react";
import { useAuth } from "@/hooks/useAuth";

const ActorSelection = () => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const dispatch = useDispatch();
  const selectedActor = useSelector((state: RootState) => state.video.actor);
  const [loading, setLoading] = useState(false);
  const avatars = useSelector((state: RootState) => state.app.avatars);
  const sortedAvatars = ['Joanna', 'Colton', 'Kayla', 'Nolan', 'Valeria', 'Bill'].map((name) => {
    return avatars.find(a => a.name === name);
  });
  const user = useAuth();

  useRollbarPerson(user);

  // user actions
  const handlePlayVideo = (actorId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    const actor = avatars.find(a => a.id === actorId);

    if (!actor?.preview_video_url) {
      toast({
        title: "Video preview not available",
        description: "Sorry, this actor doesn't have a video preview available.",
        variant: "destructive"
      });

      return;
    }

    setCurrentVideoUrl(actor.preview_video_url);
    setVideoModalOpen(true);
  };
  const handleSelectActor = (actorId: string) => {
    const actor = avatars.find(a => a.id === actorId);

    setLoading(true);

    if (!actor) {
      return;
    }

    dispatch(setVideoActor(actor));
    dispatch(clearScriptAudio());

    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  // render
  return (
    <Layout>
      <PageTransition>
        <Header />
        <ProgressBar />

        <motion.div
          className="space-y-6"
          initial={{
            opacity: 0,
            y: 20
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            delay: 0.2,
            duration: 0.5
          }}
        >
          <div className="text-center mb-6">
            <h2 className="font-bold tracking-tight mb-3 text-xl">STEP 2: Choose Your Actor</h2>
            <p className="text-muted-foreground text-balance">
              Select a professional actor who will present your property in the video ad.
            </p>
          </div>

          <div className="glass rounded-xl p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <AnimatePresence>
                {sortedAvatars.map(actor => (
                  <motion.div
                    key={actor.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className={`
                      relative overflow-hidden rounded-lg border transition-all
                      ${loading ? "cursor-wait" : "cursor-pointer"}
                      ${selectedActor.id === actor.id ? "border-primary" : "hover:border-primary/50"}
                    `}
                    onClick={() => handleSelectActor(actor.id)}
                  >
                    <div className="aspect-square relative">
                      <img src={actor.preview_image_url} alt={actor.name} className="object-cover w-full h-full" />
                      {selectedActor.id === actor.id && loading && (
                        <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1 z-10 absolute">
                          <Loader className="h-4 w-4 animate-spin" />
                        </div>
                      )}

                      {selectedActor.id === actor.id && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}

                      <div className="absolute bottom-2 right-2">
                        <ActionButton variant="play" onClick={e => handlePlayVideo(actor.id, e)} />
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-sm">{actor.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{actor.description}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="pt-4 flex flex-col space-y-2">
              <ActionButton fullWidth to={selectedActor.id ? "/new-video/script" : "#"} disabled={!selectedActor.id || loading}>
                {selectedActor.id ? "Continue to Script Selection" : "Select an Actor to Continue"}
              </ActionButton>

              <div className="flex justify-center">
                <ActionButton variant="back" to="/">
                  Back to URL Input
                </ActionButton>
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-medium mb-3">Why choose an AI actor?</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <span>Professional presentation increases viewer engagement significantly </span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <span>Clear delivery ensures your property features are properly highlighted</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="7" />
                    <path d="M8.21 13.89 7 23l5-3 5 3-1.21-9.12" />
                  </svg>
                </div>
                <span>Trained actors create a premium feel for your listing</span>
              </li>
            </ul>
          </div>
        </motion.div>

        <VideoModal isOpen={videoModalOpen} onClose={() => setVideoModalOpen(false)} videoUrl={currentVideoUrl} />
      </PageTransition>
    </Layout>
  );
};
export default ActorSelection;
